<script setup>
import { ref, onMounted } from "vue";
import { useRouter } from "vue-router";
import AppDataTable from "@/components/AppDataTable.vue";
import ConfirmDialog from "@/components/ConfirmDialog.vue";
import { retrieveMapDataList, deleteMapEntry } from "./api";

const router = useRouter();
const rows = ref([]);
const loading = ref(false);
const error = ref("");
const pendingDelete = ref(null);
const deleting = ref(false);
const columns = [
  { key: "pageName", label: "Event Name", sortable: true },
  { key: "actions",    label: "Actions",    align: "end", width: "180px" },
];

async function load() {
  loading.value = true; error.value = "";
  try {
    const data = await retrieveMapDataList();
    rows.value = (data.scriptList || []).map((s) => {
      if (typeof s === "string") return { id: s, pageName: s };
      return { id: s.scriptName ?? s.pageName, pageName: s.pageName ?? s.scriptName };
    });
  } catch { error.value = "Couldn't load event homepages."; }
  finally { loading.value = false; }
}

async function confirmDelete() {
  if (!pendingDelete.value) return;
  deleting.value = true;
  try {
    await deleteMapEntry(pendingDelete.value.id);
    rows.value = rows.value.filter((r) => r.id !== pendingDelete.value.id);
    pendingDelete.value = null;
  } catch { error.value = "Couldn't delete."; }
  finally { deleting.value = false; }
}

onMounted(load);
</script>

<template>
  <section>
    <div class="d-flex align-center justify-space-between mb-4 gap-3 flex-wrap">
      <h1 class="text-h5">Event HomePages</h1>
      <div class="d-flex gap-2">
        <VBtn variant="tonal" @click="load">Refresh</VBtn>
        <VBtn @click="router.push({ name: 'event-homepages-new' })">Add Event Page</VBtn>
      </div>
    </div>
    <VAlert v-if="error" type="error" class="mb-4">{{ error }}</VAlert>
    <AppDataTable :columns="columns" :rows="rows" :loading="loading" row-key="id" searchable search-placeholder="Search…" empty-text="No event homepages yet.">
      <template #cell-actions="{ row }">
        <VBtn size="small" variant="text" @click.stop="router.push({ name: 'event-homepages-edit', params: { id: row.id } })">Edit</VBtn>
        <VBtn size="small" color="error" variant="text" @click.stop="pendingDelete = row">Delete</VBtn>
      </template>
    </AppDataTable>
    <ConfirmDialog :model-value="pendingDelete !== null" title="Delete event page?" :message="`Delete &quot;${pendingDelete?.scriptName || 'this page'}&quot;?`" :loading="deleting" @update:model-value="pendingDelete = null" @confirm="confirmDelete" />
  </section>
</template>
